import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { apiUrl } from '../config/api';
import { apiClient } from '../lib/apiClient';
import { apiResponseSchema } from '../lib/schemas';

const runSchema = z.object({
  id: z.number().optional(),
  started_at: z.string().optional(),
  status: z.string(),
  reason: z.string().nullable(),
  message: z.string().nullable(),
  finished_at: z.string().nullable(),
  rows_deleted: z.number().optional(),
  rows_created: z.number().optional(),
});
export type ZmSyncRun = z.infer<typeof runSchema>;

const statusSchema = z.object({
  running: z.boolean(),
  started_at: z.string().optional(),
  last: runSchema.optional(),
  last_success_at: z.string().optional(),
});

export function useZmSyncStatus() {
  return useQuery({
    queryKey: ['zmSync', 'status'],
    queryFn: async () =>
      (await apiClient.get(apiUrl.zmSyncStatus(), { validate: apiResponseSchema(statusSchema), context: 'zmSyncStatus' }))
        .data,
    refetchInterval: (q) => (q.state.data?.running ? 10_000 : 60_000),
    retry: false,
  });
}

export function useZmSyncLog(running: boolean) {
  return useQuery({
    queryKey: ['zmSync', 'log'],
    queryFn: async (): Promise<ZmSyncRun[]> =>
      (
        await apiClient.get(apiUrl.adminZmSyncLog(20), {
          validate: apiResponseSchema(z.array(runSchema)),
          context: 'zmSyncLog',
        })
      ).data,
    refetchInterval: running ? 10_000 : false,
  });
}

// Manual trigger; refreshes sync status/log and time-entry data afterwards.
export function useRunZmSync() {
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);

  const run = async (): Promise<ZmSyncRun | null> => {
    setRunning(true);
    try {
      const result = await apiClient.post(apiUrl.adminZmSync(), undefined, {
        validate: apiResponseSchema(runSchema),
        context: 'adminZmSync',
        timeout: 10 * 60_000, // backend zmSyncTimeout
      });
      return result.data;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      alert(
        /already running|409/i.test(msg)
          ? 'ZM-Synchronisierung läuft bereits.'
          : `ZM-Synchronisierung fehlgeschlagen: ${msg}`
      );
      return null;
    } finally {
      setRunning(false);
      queryClient.invalidateQueries({ queryKey: ['zmSync'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['monthlyStatus'] });
    }
  };

  return { run, running };
}
